import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isToday
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Loader2, EyeOff, Eye } from 'lucide-react';
import { BookingModal } from '../../../../components/dashboard/BookingModal';
import AppointmentDetailsModal from './AppointmentDetailsModal';
import { DayDetailModal } from './DayDetailModal';

interface Appointment {
    id: string;
    start_time: string;
    end_time: string;
    customer_name: string;
    customer_phone: string;
    vehicle_vrm: string;
    vehicle_make: string;
    vehicle_model: string;
    status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
    appointment_type: string | null;
    notes: string;
    linked_job_id: string | null;
    linked_quote_id: string | null;
}

export default function CalendarView() {
    const { activeOrgId } = useVertical();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // Modals
    const [isNewApptOpen, setIsNewApptOpen] = useState(false);
    const [initialDateForNew, setInitialDateForNew] = useState<Date | undefined>(undefined);
    const [selectedAppt, setSelectedAppt] = useState<Appointment | null>(null);
    const [dayModalDate, setDayModalDate] = useState<Date | null>(null);
    // Cancelled appointments stay in the record but are hidden by default: one
    // left sitting in the grid makes a free slot look taken, which is exactly
    // why re-booking a cancelled slot felt impossible.
    const [showCancelled, setShowCancelled] = useState(false);

    // Fetch Appointments for the visible range
    const fetchAppointments = async () => {
        if (!activeOrgId) return;
        setLoading(true);

        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        // Look a bit beyond month boundaries for better UX if needed
        const viewStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const viewEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        try {
            const { data, error } = await supabase.rpc('fn_get_appointments_range', {
                p_org_id: activeOrgId,
                p_start_date: viewStart.toISOString(),
                p_end_date: viewEnd.toISOString()
            });

            if (error) throw error;
            setAppointments(data || []);
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAppointments();
    }, [currentDate, activeOrgId]);

    const activeMonthDays = eachDayOfInterval({
        start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 })
    });

    const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
    const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const goToday = () => setCurrentDate(new Date());

    const isHidden = (appt: Appointment) =>
        !showCancelled && (appt.status === 'cancelled' || appt.status === 'no_show');

    const getDayAppointments = (day: Date) =>
        appointments.filter(appt => isSameDay(new Date(appt.start_time), day) && !isHidden(appt));

    const handleNewClick = () => {
        setInitialDateForNew(new Date());
        setIsNewApptOpen(true);
    };

    const handleDateClick = (day: Date) => {
        setSelectedDate(day);
        setDayModalDate(day);
    };

    /** Opens the booking form for a day, pre-filling a sensible start time. */
    const openBookingFor = (day: Date) => {
        const now = new Date();
        const initial = new Date(day);

        // If today, set to next hour. If future, set to 9am
        if (isToday(day)) {
            initial.setHours(now.getHours() + 1, 0, 0, 0);
        } else {
            initial.setHours(9, 0, 0, 0);
        }
        setInitialDateForNew(initial);
        setDayModalDate(null);
        setIsNewApptOpen(true);
    };

    return (
        <div className="relative h-[calc(100vh-220px)] min-h-[600px] flex flex-col rounded-2xl border border-line bg-card text-fg-2 overflow-hidden shadow-card">
            {/* Calendar Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-line shrink-0">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-display font-bold text-fg flex items-center gap-2">
                        <CalendarIcon className="w-5 h-5 text-gold" />
                        {format(currentDate, 'MMMM yyyy')}
                    </h2>
                    <div className="flex bg-raised border border-line rounded-lg p-1">
                        <button onClick={prevMonth} className="p-1 hover:bg-card rounded transition-colors" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></button>
                        <button onClick={goToday} className="px-3 text-xs font-display font-semibold hover:bg-card rounded transition-colors">Today</button>
                        <button onClick={nextMonth} className="p-1 hover:bg-card rounded transition-colors" aria-label="Next month"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                <button
                    onClick={() => setShowCancelled(v => !v)}
                    title={showCancelled ? 'Hide cancelled appointments' : 'Show cancelled appointments'}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-line text-fg-3 hover:text-fg hover:bg-raised transition-colors text-xs font-display font-semibold"
                >
                    {showCancelled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <span className="hidden sm:inline">Cancelled</span>
                </button>
                <button
                    onClick={handleNewClick}
                    className="flex items-center gap-2 fd-btn-primary px-4 py-2 rounded-lg font-display font-semibold transition-colors text-sm"
                >
                    <Plus className="w-4 h-4" />
                    New Appointment
                </button>
                </div>
            </div>

            {/* Calendar Grid Header */}
            <div className="grid grid-cols-7 border-b border-line bg-raised shrink-0">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="py-2 text-center text-fd-3xs font-display font-semibold text-fg-3 uppercase tracking-[0.16em]">
                        {day}
                    </div>
                ))}
            </div>

            {/* Monthly Grid */}
            <div className="flex-1 grid grid-cols-7 grid-rows-6 overflow-hidden">
                {activeMonthDays.map((day) => {
                    const dayAppts = getDayAppointments(day);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isTodayDate = isToday(day);

                    return (
                        <div
                            key={day.toString()}
                            onClick={() => handleDateClick(day)}
                            className={`
                                border-b border-r border-line p-2 relative group cursor-pointer transition-colors
                                ${!isCurrentMonth ? 'bg-canvas/40 text-fg-4' : 'bg-transparent'}
                                ${isSelected ? 'bg-gold-fill/10' : 'hover:bg-raised'}
                            `}
                        >
                            {/* Day Number */}
                            <div className={`
                                text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full tabular-nums
                                ${isTodayDate ? 'fd-btn-primary font-bold' : 'text-fg-3'}
                            `}>
                                {format(day, 'd')}
                            </div>

                            {/* Appointment summary — a month cell can't show three
                                bookings legibly on a phone, so it shows how many
                                and the day modal shows what they are. */}
                            {dayAppts.length > 0 && (
                                <div className="space-y-1">
                                    {/* "Booked" counts only what is still coming.
                                        A finished appointment stayed in the amber
                                        count, so a day whose work was all done
                                        still read as a full day ahead. Completed
                                        gets its own quiet green tally. */}
                                    {dayAppts.some(a => a.status === 'scheduled') && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gold-fill/10 border border-gold/30 text-fd-3xs font-semibold text-gold tabular-nums">
                                            {dayAppts.filter(a => a.status === 'scheduled').length} booked
                                        </span>
                                    )}
                                    {dayAppts.some(a => a.status === 'completed') && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-success/10 border border-success/30 text-fd-3xs font-semibold text-success tabular-nums">
                                            {dayAppts.filter(a => a.status === 'completed').length} done
                                        </span>
                                    )}
                                    <div className="hidden sm:block space-y-0.5">
                                        {dayAppts.slice(0, 2).map(appt => (
                                            <div
                                                key={appt.id}
                                                className={`text-fd-3xs px-1 truncate ${appt.status === 'completed' ? 'text-fg-4 line-through' : 'text-fg-3'}`}
                                            >
                                                <span className={`font-semibold tabular-nums ${appt.status === 'completed' ? 'text-fg-4' : 'text-fg-2'}`}>
                                                    {format(new Date(appt.start_time), 'HH:mm')}
                                                </span>{' '}
                                                {appt.customer_name}
                                            </div>
                                        ))}
                                        {dayAppts.length > 2 && (
                                            <div className="text-fd-3xs text-fg-4 pl-1">
                                                +{dayAppts.length - 2} more
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Add Button (Hover) */}
                            <button
                                className="absolute top-2 right-2 fd-hover-reveal p-1 bg-card hover:bg-raised rounded-full"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openBookingFor(day);
                                }}
                                aria-label="Add appointment"
                            >
                                <Plus className="w-3 h-3 text-fg-3" />
                            </button>
                        </div>
                    );
                })}
            </div>
            {loading && (
                <div className="absolute inset-0 bg-scrim/50 backdrop-blur-sm flex items-center justify-center z-10">
                    <Loader2 className="w-8 h-8 text-gold animate-spin" />
                </div>
            )}

            {/* Modals */}
            <BookingModal
                isOpen={isNewApptOpen}
                onClose={() => setIsNewApptOpen(false)}
                onSuccess={() => {
                    setIsNewApptOpen(false);
                    fetchAppointments();
                }}
                initialDate={initialDateForNew}
            />

            <DayDetailModal
                day={dayModalDate}
                appointments={dayModalDate ? getDayAppointments(dayModalDate) : []}
                onClose={() => setDayModalDate(null)}
                onSelect={(appt) => { setDayModalDate(null); setSelectedAppt(appt as Appointment); }}
                onBook={openBookingFor}
            />

            <AppointmentDetailsModal
                isOpen={!!selectedAppt}
                appointment={selectedAppt}
                onClose={() => setSelectedAppt(null)}
                onUpdate={() => {
                    setSelectedAppt(null);
                    fetchAppointments();
                }}
            />
        </div>
    );
}
